import type { CredentialResponse } from "@react-oauth/google";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { readPendingWorkflow } from "@replohq/lattice/blocks/workflow-checklist/pendingWorkflow";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { cn } from "@replohq/lattice/utils/cn";
import {
  authButtonClassName,
  authFieldClassName,
} from "@v2/features/account/authFieldStyles";
import { AuthMethodDivider } from "@v2/features/account/AuthMethodDivider";
import {
  Screen,
  useNextUrlWithPendingAppInstallationId,
} from "@v2/features/account/common";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import { getProjectLandingPath } from "@v2/features/account/postLoginLanding";
import { useTheme } from "@v2/hooks/useTheme";
import { authRouter } from "@v2/trpc/routes/auth";
import { userRouter } from "@v2/trpc/routes/user";
import { storeToken } from "@v2/utils/store-token";
import { useForm, useWatch } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import {
  clearLastVisitedDashboardRoute,
  getLandingUserQueryInput,
  setLastVisitedDashboardRoute,
} from "replo-utils/lib/lastVisitedDashboardIds";
import { isEmpty } from "replo-utils/lib/misc";
import { isOsmanthusEnabled } from "replo-utils/lib/osmanthus";
import { z } from "zod";

import { getMarketingWorkflow } from "./marketingWorkflow";

const emailSchema = z.email();

type LoginFormValues = {
  email: string;
  password: string;
};

const LoginForm: React.FC = () => {
  const { effectiveMode } = useTheme();
  const isOsmanthus = isOsmanthusEnabled();
  const [searchParams, setSearchParams] = useSearchParams();

  const { nextHash, nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  const userUtils = userRouter.useUtils();

  // Resolve the last project's dashboard before leaving /auth/login so the user
  // gets one document load instead of the legacy app booting just to compute
  // the same redirect (REPL-30166). `next` deep links and pending Shopify install params
  // keep the legacy landing (`useNextUrl` defaults `nextUrl` to "/" when `next`
  // is absent, so presence must be checked on the search params). `staleTime: 0`
  // bypasses the logged-out `null` that CurrentUserProvider already cached for
  // this query on the login screen. Any fetch failure falls back to the legacy
  // landing unchanged. External links also need this fetch to issue the browser
  // session cookie before leaving the dashboard.
  const handleLogin = async (token: string) => {
    storeToken(token);
    const isLegacyLandingFlow =
      searchParams.get("next") !== null ||
      searchParams.get("shopifyIntegrationId") !== null ||
      searchParams.get("pendingAppInstallationId") !== null;
    const isExternalLanding = Boolean(nextUrl && /^https?:\/\//.test(nextUrl));
    let landingPath = nextUrl;
    if (!isLegacyLandingFlow || isExternalLanding) {
      const landingUserQueryInput = getLandingUserQueryInput();
      // A same-key anonymous request may still be in flight from the login
      // screen. Cancel it so this fetch cannot dedupe onto its logged-out result.
      await userUtils.get.cancel(landingUserQueryInput);
      const user = await userUtils.get
        .fetch(landingUserQueryInput, { staleTime: 0 })
        .catch(() => null);
      if (!isLegacyLandingFlow) {
        if (user?.defaultProjectId) {
          setLastVisitedDashboardRoute({
            id: user.defaultProjectId,
            hostingType: user.defaultProjectHostingType ?? null,
            agentEnabledAt: user.defaultProjectAgentEnabledAt,
          });
        } else {
          clearLastVisitedDashboardRoute();
        }
        // "/" sends a user with no project to onboarding.
        landingPath = getProjectLandingPath(user) ?? "/";
      }
    }
    const searchString = nextSearchParams.toString();
    window.location.replace(
      `${landingPath}${searchString ? `?${searchString}` : ""}${nextHash}`,
    );
  };

  const {
    mutate: loginApi,
    isPending: isLoadingLogin,
    error: loginError,
  } = authRouter.login.useMutation({
    onSuccess: ({ token }) => void handleLogin(token),
  });

  const { mutate: loginWithGoogleApi, error: googleLoginError } =
    authRouter.loginWithGoogle.useMutation({
      onSuccess: ({ token }) => void handleLogin(token),
    });

  // Note (Kevin, 2026-01-03, REPL-21631): Prefill test credentials on localhost
  const shouldPrefillTestCredentials = isLocalhost();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: shouldPrefillTestCredentials
        ? "test@replo.app"
        : searchParams.get("email") ?? "",
      password: shouldPrefillTestCredentials ? "12345678" : "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });

  const handleGoogleCredentials = async (
    credentialResponse: CredentialResponse,
  ) => {
    const { credential, clientId } = credentialResponse;
    if (credential && clientId) {
      const pendingTemplateId = sessionStorage.getItem(
        "replo_pending_template_id",
      );
      loginWithGoogleApi({
        credential,
        clientId,
        ...(pendingTemplateId ? { pendingTemplateId } : {}),
      });
    }
  };

  const onSubmit = async ({ email, password }: LoginFormValues) => {
    const pendingTemplateId = sessionStorage.getItem(
      "replo_pending_template_id",
    );
    loginApi({
      email,
      rawPassword: password,
      ...(pendingTemplateId ? { pendingTemplateId } : {}),
    });
  };

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;
  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  const isWorkflowAuth = Boolean(
    isOsmanthusEnabled() &&
      (getMarketingWorkflow(searchParams) || readPendingWorkflow()),
  );
  const loginErrorMessage = loginError?.message ?? googleLoginError?.message;
  let loginHeading: React.ReactNode = (
    <>
      <h1 className="font-editorial text-3xl font-bold text-default">
        Sell <span className="font-editorial font-light italic">Anything</span>{" "}
        with Replo
      </h1>
      <p className="text-sm text-muted">
        Sign in to pick up where you left off.
      </p>
    </>
  );
  if (isWorkflowAuth) {
    loginHeading = null;
  }
  const shouldShowShopifyLoginInlineAlert =
    searchParams.get("error") === "login_in_to_associate_shopify";

  const removeSearchParam = () => {
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.delete("error");
      return newParams;
    });
  };

  return (
    <form
      className="no-scrollbar"
      onSubmit={(data) => {
        if (shouldShowShopifyLoginInlineAlert) {
          removeSearchParam();
        }
        void handleSubmit(onSubmit)(data);
      }}
    >
      <div
        className={cn(
          "mb-5 flex flex-col justify-center gap-2",
          isWorkflowAuth ? "hidden" : "min-h-[6.5rem] items-center text-center",
        )}
      >
        {loginHeading}
      </div>
      {(loginErrorMessage || shouldShowShopifyLoginInlineAlert) && (
        <Alert
          variant={
            shouldShowShopifyLoginInlineAlert && !loginErrorMessage
              ? "warning"
              : "error"
          }
          UNSAFE_className="mb-2"
          description={
            loginErrorMessage ??
            "Please create or log in to your Replo account to continue to Replo."
          }
        />
      )}
      <div className="flex flex-col gap-4">
        <LabeledControl label="Email" size="base" id="email" error={emailError}>
          <Input
            id="email"
            UNSAFE_className={isOsmanthus ? authFieldClassName : undefined}
            invalid={Boolean(emailError)}
            autoComplete="off"
            placeholder="Your Work Email"
            {...register("email", {
              required: "Please enter a valid email address.",
              validate: (value) => {
                const parsedEmail = emailSchema.safeParse(value);
                return (
                  parsedEmail.success || "Please enter a valid email address"
                );
              },
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("email"),
            })}
            type="email"
            size="base"
          />
        </LabeledControl>
        <LabeledControl
          label="Password"
          id="password"
          labelAction={
            <Link
              to={
                watchAllFields.email
                  ? `${Screen.INIT_RESET_PASSWORD}?email=${encodeURIComponent(
                      watchAllFields.email,
                    )}`
                  : Screen.INIT_RESET_PASSWORD
              }
              className={cn(
                "text-sm font-medium hover:underline",
                isOsmanthus ? "text-default" : "text-primary",
              )}
            >
              Forgot Password?
            </Link>
          }
          size="base"
          error={passwordError}
        >
          <Input
            id="password"
            UNSAFE_className={isOsmanthus ? authFieldClassName : undefined}
            autoComplete="off"
            placeholder="••••••••••••"
            invalid={Boolean(passwordError)}
            {...register("password", {
              required: "Please enter a valid password",
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("password"),
            })}
            type="password"
            size="base"
          />
        </LabeledControl>
      </div>
      <div className="mt-10 flex flex-col gap-y-4 overflow-hidden">
        <Button
          className="w-full"
          UNSAFE_className={isOsmanthus ? authButtonClassName : undefined}
          variant="primary"
          size="lg"
          type="submit"
          loading={isLoadingLogin}
        >
          {isWorkflowAuth ? "Sign in and continue" : "Login"}
        </Button>
        {isOsmanthus ? <AuthMethodDivider /> : <Separator />}
        <GoogleAuth
          handleGoogleCredentials={handleGoogleCredentials}
          effectiveMode={effectiveMode}
        />
      </div>
    </form>
  );
};

export default LoginForm;
